import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { dialogFormColumn } from '../../../theme/mixins';

export const dialogTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    pb: 1,
};

/** Lets the fields scroll under a pinned footer -- see `dialogFormColumn`. */
export const dialogFormStyles: SxProps<Theme> = dialogFormColumn;

export const descriptionStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mb: 3,
};

export const formAlertStyles: SxProps<Theme> = {
    mb: 2,
    borderRadius: 2,
};

export const fieldsBoxStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
};

/**
 * The three money fields. They stay on one row from `sm` up so the arithmetic between
 * them is visible at a glance, and stack on a phone where three number inputs side by
 * side are too narrow to type into.
 */
export const priceRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 2,
};

/**
 * The session count beside the date.
 *
 * Wraps rather than shrinks, which is the same recipe `dateTimeRowStyles` settles on: a
 * plain row squeezed the picker to about 165px on a tablet, and a date field that narrow
 * has no room left for its own calendar button. Wrapping also stacks the pair on a phone,
 * so the breakpointed `flexDirection` this replaced is not needed.
 */
/**
 * The total, which is a figure rather than a field.
 *
 * It keeps the outlined box so the row still reads as three columns of one sum, but nothing
 * about it is interactive. `readOnly` alone was not enough: the box still took focus, drew
 * the focus ring around itself and let its text be selected, which reads as an invitation to
 * type into it -- and typing into it does nothing, which is the worse half of the bug this
 * replaced. Paired with `tabIndex: -1` at the call site so the keyboard skips it too.
 */
export const derivedPriceStyles: SxProps<Theme> = {
    pointerEvents: 'none',
    '& .MuiInputBase-input': {
        userSelect: 'none',
    },
};

export const twoUpStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    alignItems: 'flex-start',
};

/** `min(220px, 100%)` rather than a bare basis, so neither child can push the dialog sideways. */
export const twoUpItemStyles: SxProps<Theme> = {
    flex: '1 1 min(220px, 100%)',
    minWidth: 0,
};

/**
 * The tick and the hour behind it, kept as one block so the time reads as belonging to
 * the question above it rather than as a field of its own.
 */
export const sessionBlockStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
    mt: -1,
};

/** Sits under the tick, explaining what ticking it writes down. */
export const sessionHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mt: -1,
};

/** Narrow: five characters, and a full-width time input reads as a much bigger ask. */
export const sessionTimeStyles: SxProps<Theme> = {
    width: { xs: '100%', sm: 200 },
};

/** The derived per-session figure, sitting under the price fields as a sanity check. */
export const perSessionStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mt: -1,
};

export const dialogActionsStyles: SxProps<Theme> = {
    px: 3,
    pb: 3,
    pt: 1,
};
